import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  CalendarEventDto,
  MeetingBotDto,
  MeetingBotDtoStatus,
  UserDetails,
  UserMeetingSettingsMeetingsToJoin
} from '@/api/generated';

dayjs.extend(utc);

const getMeetingsToJoinFlags = (currentUser: UserDetails | null) => ({
  isMeetingsToJoinNone:
    currentUser?.meetingSetting?.meetingsToJoin === UserMeetingSettingsMeetingsToJoin.none,
  isMeetingsToJoinHost:
    currentUser?.meetingSetting?.meetingsToJoin === UserMeetingSettingsMeetingsToJoin.host,
  isMeetingsToJoinInternal:
    currentUser?.meetingSetting?.meetingsToJoin === UserMeetingSettingsMeetingsToJoin.internal
});

const isMeetingExternal = (event: CalendarEventDto, userEmail: string) => {
  const orgenizerDomain = event.organizer?.email?.split('@')[1];
  const currentUserDomain = userEmail.split('@')[1];

  if (orgenizerDomain && orgenizerDomain !== currentUserDomain) return true;

  const isExternal = event.participants?.some(p => p.email?.split('@')[1] !== currentUserDomain);

  return isExternal;
};

const isUserTheHost = (userEmail: string, event: CalendarEventDto) => {
  const organizerEmail = event.organizer?.email;
  if (!organizerEmail) return false;

  return userEmail === organizerEmail;
};

export const createScheduleObject = (event: CalendarEventDto, currentUser: UserDetails | null) => ({
  meetingUrl: event.conferencing?.url,
  calendarEventId: event.id,
  calendarMasterEventId: event.masterEventId,
  isManual: true,
  scheduleAt: event.when?.startTime
    ? dayjs.unix(event.when.startTime).utc().toISOString()
    : undefined,
  accountId: currentUser?.accounts?.[0]?.id,
  userId: currentUser?.id
});

export const getIsEventInviteable = (
  event: CalendarEventDto,
  currentUser: UserDetails | null,
  userEmail: string | undefined
) => {
  if (!event.conferencing?.url || !currentUser || !event.when?.startTime || !userEmail)
    return false;

  const isInFuture =
    dayjs().isBefore(dayjs.unix(event.when.startTime)) &&
    !dayjs().isAfter(dayjs.unix(event.when.startTime).subtract(5, 'minutes'));

  if (!isInFuture) return false;

  const meetingsToJoinFlags = getMeetingsToJoinFlags(currentUser);
  if (meetingsToJoinFlags.isMeetingsToJoinNone) return true;

  const isExternal =
    meetingsToJoinFlags.isMeetingsToJoinInternal && isMeetingExternal(event, userEmail);
  const isNotHost = meetingsToJoinFlags.isMeetingsToJoinHost && !isUserTheHost(userEmail, event);
  return isExternal || isNotHost;
};

export const getLatestScheduledMeetingBot = (
  eventId: string | undefined,
  meetingBots: MeetingBotDto[]
) => {
  if (!eventId) return null;
  const bots = meetingBots
    ?.filter(bot => bot.calendarEventId === eventId && bot.status === MeetingBotDtoStatus.scheduled)
    .sort((a, b) => Date.parse(a.createdAt || '') - Date.parse(b.createdAt || ''));
  return bots?.[bots.length - 1] || null;
};
